import React, { useState, useEffect, useRef } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { useCart } from '../context/CartContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetClose } from '../components/ui/sheet';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { toast } from 'sonner';
import { ShoppingCart, Plus, Minus, Trash2, ArrowRight, Loader2, MapPin, Store } from 'lucide-react';
import axios from 'axios';
import { PayPalCheckout } from '../components/PayPalCheckout';
import TransferPaymentBlock from '../components/TransferPaymentBlock';
import OptionGroupsModal from '../components/OptionGroupsModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KosherFoodPage = () => {
  const { t, language } = useLanguage();
  const { items: cartItems, addItem, removeItem, updateQuantity, clearCart, total, itemCount } = useCart();
  
  const [menuItems, setMenuItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkoutStep, setCheckoutStep] = useState('menu');
  const [deliveryZones, setDeliveryZones] = useState({});
  const [pickupAddress, setPickupAddress] = useState('');
  const [sheetOpen, setSheetOpen] = useState(false);
  const [currency, setCurrency] = useState('MXN');
  const USD_RATE = 0.058;
  const [optionModal, setOptionModal] = useState(null); // menu item being selected
  
  const [formData, setFormData] = useState({
    customerName: '',
    phoneCode: '+52',
    customerPhone: '',
    customerEmail: '',
    deliveryType: 'delivery',
    deliveryZone: 'metropolitana',
    deliveryAddress: '',
    notes: ''
  });

  const getLocalizedField = (item, field) => {
    return item[`${field}_${language}`] || item[`${field}_es`] || item[field];
  };

  const getZoneName = (zone) => {
    if (!deliveryZones[zone]) return zone;
    return deliveryZones[zone][`name_${language}`] || deliveryZones[zone].name_es;
  };

  // Fetch menu and delivery zones
  useEffect(() => {
    const fetchData = async () => {
      try {
        await axios.post(`${API}/seed/menu`);
        
        const [menuResponse, zonesResponse] = await Promise.all([
          axios.get(`${API}/menu`),
          axios.get(`${API}/delivery-zones`)
        ]);
        
        setMenuItems(menuResponse.data);
        setDeliveryZones(zonesResponse.data.zones);
        setPickupAddress(zonesResponse.data.pickup_address);
      } catch (error) {
        console.error('Error fetching data:', error);
        toast.error(t('error'));
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [t]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Subtotal de items en la moneda seleccionada (usa price_usd / extra_price_usd cuando USD)
  const getItemsSubtotal = () => {
    if (currency === 'USD') {
      return cartItems.reduce((sum, item) => {
        const base = item.price_usd != null ? item.price_usd : item.price * USD_RATE;
        let extraUsd = 0;
        if (item.selectedOptions && item.selectedOptions.length > 0) {
          extraUsd = item.selectedOptions.reduce((s, o) => s + (Number(o.extra_price_usd) || (Number(o.extra_price) || 0) * USD_RATE), 0);
        } else {
          extraUsd = item.extraPriceUsd != null ? item.extraPriceUsd : (item.extraPrice || 0) * USD_RATE;
        }
        return sum + (base + extraUsd) * item.quantity;
      }, 0);
    }
    return total; // MXN ya calculado por CartContext
  };

  // Costo de envío convertido a la moneda seleccionada (si la zona no tiene price_usd, convierte)
  const getDeliveryCostInCurrency = () => {
    if (formData.deliveryType === 'pickup') return 0;
    const zone = deliveryZones[formData.deliveryZone];
    if (!zone) return 0;
    if (currency === 'USD') {
      return zone.price_usd != null ? zone.price_usd : zone.price * USD_RATE;
    }
    return zone.price;
  };

  const getTotalWithDelivery = () => {
    return getItemsSubtotal() + getDeliveryCostInCurrency();
  };

  const validateCheckoutForm = () => {
    if (!formData.customerName || !formData.customerPhone) {
      toast.error('Por favor complete los campos requeridos');
      return false;
    }
    if (!formData.customerEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.customerEmail.trim())) {
      toast.error('Ingresa un email válido (es necesario para enviarte la confirmación y el link de seguimiento)');
      return false;
    }
    if (formData.deliveryType === 'delivery' && !formData.deliveryAddress) {
      toast.error('Por favor ingrese su dirección de entrega');
      return false;
    }
    if (cartItems.length === 0) {
      toast.error('Carrito vacío');
      return false;
    }
    return true;
  };

  // Crea la orden local y devuelve el id + el total real (desde el backend) en la moneda seleccionada
  const createLocalOrderForPaypal = async () => {
    const orderPayload = {
      items: cartItems.map(item => ({
        menu_item_id: item.id,
        quantity: item.quantity,
        selected_option: item.selectedOption || null,
        selected_options: item.selectedOptions || null,
      })),
      delivery_zone: formData.deliveryType === 'pickup' ? 'pickup' : formData.deliveryZone,
      delivery_address: formData.deliveryType === 'pickup' ? null : formData.deliveryAddress,
      customer_name: formData.customerName,
      customer_phone: `${formData.phoneCode} ${formData.customerPhone}`,
      customer_email: formData.customerEmail || null,
      notes: formData.notes || null,
      language: language || 'es'
    };
    const orderResponse = await axios.post(`${API}/orders`, orderPayload);
    // El backend calcula el total auténtico en ambas monedas desde la BD
    let amount;
    if (currency === 'USD') {
      amount = orderResponse.data.total_amount_usd ?? Math.round(orderResponse.data.total_amount * USD_RATE * 100) / 100;
    } else {
      amount = orderResponse.data.total_amount;
    }
    amount = Math.round(amount * 100) / 100;
    return {
      referenceId: orderResponse.data.id,
      amount,
    };
  };

  const formatPrice = (price, priceUsd) => {
    if (currency === 'USD') {
      if (priceUsd) return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(priceUsd);
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(price * USD_RATE);
    }
    return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(price);
  };

  const formatPriceMXN = (price) => {
    return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(price);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAF9F6] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#B85042]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF9F6] pt-20 pb-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading text-3xl sm:text-4xl font-bold text-[#0F2922]" data-testid="page-title">
              Elisheva&apos;s Kosher
            </h1>
            <p className="font-body text-[#5C6C62] mt-2">{t('menu')}</p>
          </div>

          {/* Cart Button */}
          <div className="flex items-center gap-3">
            {/* Currency Toggle */}
            <div className="flex items-center bg-white border border-[#E5E3D8] rounded-xl overflow-hidden">
              <button
                onClick={() => setCurrency('MXN')}
                className={`px-3 py-2 text-xs font-bold transition-colors ${currency === 'MXN' ? 'bg-[#0F2922] text-white' : 'text-[#5C6C62]'}`}
                data-testid="currency-mxn"
              >
                MXN
              </button>
              <button
                onClick={() => setCurrency('USD')}
                className={`px-3 py-2 text-xs font-bold transition-colors ${currency === 'USD' ? 'bg-[#0F2922] text-white' : 'text-[#5C6C62]'}`}
                data-testid="currency-usd"
              >
                USD
              </button>
            </div>

            <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
              <SheetTrigger asChild>
                <Button 
                  variant="outline" 
                  className="relative border-[#E5E3D8] hover:border-[#B85042] hover:text-[#B85042]"
                  data-testid="cart-button"
                >
                  <ShoppingCart className="w-5 h-5" />
                  {itemCount > 0 && (
                    <span className="absolute -top-2 -right-2 w-5 h-5 bg-[#B85042] text-white text-xs rounded-full flex items-center justify-center">
                      {itemCount}
                    </span>
                  )}
                </Button>
              </SheetTrigger>
            <SheetContent className="w-full sm:max-w-md bg-white" data-testid="cart-sheet">
              <SheetHeader>
                <SheetTitle className="font-heading text-[#0F2922]">{t('cart')}</SheetTitle>
              </SheetHeader>
              
              <div className="mt-6 flex flex-col h-[calc(100vh-200px)]">
                {cartItems.length === 0 ? (
                  <p className="text-center text-[#5C6C62] font-body py-8">{t('emptyCart')}</p>
                ) : (
                  <>
                    <div className="flex-1 overflow-y-auto space-y-4">
                      {cartItems.map((item) => (
                        <div 
                          key={item.cartKey} 
                          className="flex items-center gap-4 p-4 bg-[#FAF9F6] rounded-xl"
                          data-testid={`cart-item-${item.cartKey}`}
                        >
                          <div className="flex-1">
                            <h4 className="font-body font-medium text-[#0F2922]">
                              {getLocalizedField(item, 'name')}
                            </h4>
                            {item.selectedOptions && item.selectedOptions.length > 0 && (
                              <p className="text-xs text-[#B85042] font-medium leading-snug mt-0.5">
                                {item.selectedOptions.map(o => o[`label_${language}`] || o.label_es).join(', ')}
                              </p>
                            )}
                            {!item.selectedOptions && item.selectedOption && (
                              <p className="text-xs text-[#B85042] font-medium">{item.selectedOption}</p>
                            )}
                            <p className="text-sm text-[#5C6C62] mt-0.5">{(() => {
                              const extraMxn = (item.selectedOptions || []).reduce((s, o) => s + (Number(o.extra_price) || 0), 0) || (item.extraPrice || 0);
                              const extraUsd = (item.selectedOptions || []).reduce((s, o) => s + (Number(o.extra_price_usd) || (Number(o.extra_price) || 0) * USD_RATE), 0) || (item.extraPriceUsd != null ? item.extraPriceUsd : (item.extraPrice || 0) * USD_RATE);
                              const baseMxn = item.price + extraMxn;
                              const baseUsd = (item.price_usd != null ? item.price_usd : item.price * USD_RATE) + extraUsd;
                              return currency === 'USD' ? `$${baseUsd.toFixed(2)} USD` : `$${baseMxn.toFixed(2)} MXN`;
                            })()}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => updateQuantity(item.cartKey, item.quantity - 1)}
                              data-testid={`decrease-${item.cartKey}`}
                            >
                              <Minus className="w-4 h-4" />
                            </Button>
                            <span className="w-8 text-center font-medium">{item.quantity}</span>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => updateQuantity(item.cartKey, item.quantity + 1)}
                              data-testid={`increase-${item.cartKey}`}
                            >
                              <Plus className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-red-500 hover:text-red-600"
                              onClick={() => removeItem(item.cartKey)}
                              data-testid={`remove-${item.cartKey}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    <div className="border-t border-[#E5E3D8] pt-4 mt-4">
                      <div className="flex justify-between items-center mb-2">
                        <span className="font-body text-[#5C6C62]">{t('subtotal')}</span>
                        <span className="font-body text-[#0F2922]">{currency === 'USD' ? `$${getItemsSubtotal().toFixed(2)} USD` : `$${getItemsSubtotal().toFixed(2)} MXN`}</span>
                      </div>
                      <Button
                        className="w-full bg-[#B85042] hover:bg-[#B85042]/90 text-white rounded-xl py-6"
                        onClick={() => { setSheetOpen(false); setTimeout(() => setCheckoutStep('details'), 300); }}
                        data-testid="checkout-button"
                      >
                        {t('checkout')}
                        <ArrowRight className="w-5 h-5 ml-2" />
                      </Button>
                    </div>
                  </>
                )}
              </div>
            </SheetContent>
          </Sheet>
          </div>
        </div>

        {/* Checkout Form Modal */}
        {checkoutStep === 'details' && (
          <div className="fixed inset-0 bg-black/50 z-[60] overflow-y-auto" onClick={(e) => e.target === e.currentTarget && setCheckoutStep('menu')}>
            <div className="min-h-full flex items-start justify-center py-8 px-4">
              <Card className="w-full max-w-lg bg-white border-[#E5E3D8] rounded-2xl relative" data-testid="checkout-form">
                <CardHeader>
                  <CardTitle className="font-heading text-[#0F2922]">{t('checkout')}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Customer Info */}
                  <div>
                    <Label htmlFor="customerName" className="text-[#0F2922] font-body">{t('customerName')} *</Label>
                  <Input
                    id="customerName"
                    name="customerName"
                    value={formData.customerName}
                    onChange={handleInputChange}
                    className="mt-1 border-[#E5E3D8] rounded-xl"
                    required
                    data-testid="input-name"
                  />
                </div>
                <div>
                  <Label htmlFor="customerPhone" className="text-[#0F2922] font-body">{t('customerPhone')} *</Label>
                  <div className="flex gap-2 mt-1">
                    <select
                      value={formData.phoneCode}
                      onChange={(e) => setFormData(prev => ({ ...prev, phoneCode: e.target.value }))}
                      className="w-[100px] h-10 px-2 border border-[#E5E3D8] rounded-xl bg-white text-sm text-[#0F2922] font-body"
                      data-testid="input-phone-code"
                    >
                      <option value="+52">+52 MX</option>
                      <option value="+1">+1 US/CA</option>
                      <option value="+972">+972 IL</option>
                      <option value="+44">+44 UK</option>
                      <option value="+33">+33 FR</option>
                      <option value="+49">+49 DE</option>
                      <option value="+55">+55 BR</option>
                      <option value="+54">+54 AR</option>
                      <option value="+57">+57 CO</option>
                      <option value="+34">+34 ES</option>
                    </select>
                    <Input
                      id="customerPhone"
                      name="customerPhone"
                      type="tel"
                      value={formData.customerPhone}
                      onChange={handleInputChange}
                      className="flex-1 border-[#E5E3D8] rounded-xl"
                      placeholder="333 146 3068"
                      required
                      data-testid="input-phone"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="customerEmail" className="text-[#0F2922] font-body">{t('customerEmail')}</Label>
                  <Input
                    id="customerEmail"
                    name="customerEmail"
                    type="email"
                    required
                    value={formData.customerEmail}
                    onChange={handleInputChange}
                    className="mt-1 border-[#E5E3D8] rounded-xl"
                    data-testid="input-email"
                    placeholder="tu@email.com"
                  />
                </div>

                {/* Delivery Type */}
                <div className="border-t border-[#E5E3D8] pt-4">
                  <Label className="text-[#0F2922] font-body font-medium mb-3 block">{t('deliveryType')}</Label>
                  <RadioGroup
                    value={formData.deliveryType}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, deliveryType: value }))}
                    className="space-y-3"
                  >
                    <label htmlFor="pickup" className="flex items-center space-x-3 p-4 bg-[#FAF9F6] rounded-xl cursor-pointer hover:bg-[#FAF9F6]/80">
                      <RadioGroupItem value="pickup" id="pickup" data-testid="radio-pickup" />
                      <div className="flex items-center gap-2 flex-1">
                        <Store className="w-5 h-5 text-[#25D366]" />
                        <div>
                          <span className="font-medium text-[#0F2922]">{t('pickup')}</span>
                          <span className="ml-2 text-[#25D366] font-bold">{t('free')}</span>
                        </div>
                      </div>
                    </label>
                    <label htmlFor="delivery" className="flex items-center space-x-3 p-4 bg-[#FAF9F6] rounded-xl cursor-pointer hover:bg-[#FAF9F6]/80">
                      <RadioGroupItem value="delivery" id="delivery" data-testid="radio-delivery" />
                      <div className="flex items-center gap-2 flex-1">
                        <MapPin className="w-5 h-5 text-[#B85042]" />
                        <span className="font-medium text-[#0F2922]">{t('delivery')}</span>
                      </div>
                    </label>
                  </RadioGroup>
                </div>

                {/* Pickup Address */}
                {formData.deliveryType === 'pickup' && (
                  <div className="p-4 bg-[#25D366]/10 rounded-xl">
                    <p className="font-body text-sm font-medium text-[#0F2922] mb-1">{t('pickupAddress')}:</p>
                    <p className="font-body text-[#5C6C62]">{pickupAddress}</p>
                  </div>
                )}

                {/* Delivery Zone Selection */}
                {formData.deliveryType === 'delivery' && (
                  <>
                    <div>
                      <Label className="text-[#0F2922] font-body">{t('selectZone')} *</Label>
                      <select
                        value={formData.deliveryZone}
                        onChange={(e) => setFormData(prev => ({ ...prev, deliveryZone: e.target.value }))}
                        className="mt-1 w-full h-10 px-3 border border-[#E5E3D8] rounded-xl bg-white text-sm text-[#0F2922] font-body"
                        data-testid="select-zone"
                      >
                        {Object.entries(deliveryZones)
                          .filter(([key]) => key !== 'pickup')
                          .map(([key, zone]) => (
                            <option key={key} value={key}>
                              {getZoneName(key)} - {currency === 'USD' && zone.price_usd ? `$${zone.price_usd} USD` : `$${zone.price} MXN`}
                            </option>
                          ))}
                      </select>
                    </div>
                    <div>
                      <Label htmlFor="deliveryAddress" className="text-[#0F2922] font-body">{t('deliveryAddress')} *</Label>
                      <Textarea
                        id="deliveryAddress"
                        name="deliveryAddress"
                        value={formData.deliveryAddress}
                        onChange={handleInputChange}
                        className="mt-1 border-[#E5E3D8] rounded-xl"
                        rows={2}
                        placeholder={language === 'es' ? 'Hotel, calle, numero, colonia... Incluye numero de habitacion si aplica' : language === 'en' ? 'Hotel, street, number... Include room number if applicable' : 'מלון, רחוב, מספר... כולל מספר חדר'}
                        required
                        data-testid="input-address"
                      />
                    </div>
                  </>
                )}

                <div>
                  <Label htmlFor="notes" className="text-[#0F2922] font-body">{t('notes')}</Label>
                  <Textarea
                    id="notes"
                    name="notes"
                    value={formData.notes}
                    onChange={handleInputChange}
                    className="mt-1 border-[#E5E3D8] rounded-xl"
                    rows={2}
                    data-testid="input-notes"
                  />
                </div>

                {/* Order Summary */}
                <div className="border-t border-[#E5E3D8] pt-4 mt-4 space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="font-body text-[#5C6C62]">{t('subtotal')}</span>
                    <span className="font-body text-[#0F2922]">{currency === 'USD' ? `$${getItemsSubtotal().toFixed(2)} USD` : `$${getItemsSubtotal().toFixed(2)} MXN`}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="font-body text-[#5C6C62]">{t('deliveryCost')}</span>
                    <span className={`font-body ${getDeliveryCostInCurrency() === 0 ? 'text-[#25D366]' : 'text-[#0F2922]'}`}>
                      {getDeliveryCostInCurrency() === 0 ? t('free') : `$${getDeliveryCostInCurrency().toFixed(2)} ${currency}`}
                    </span>
                  </div>
                  <div className="flex justify-between items-center pt-2 border-t border-[#E5E3D8]">
                    <span className="font-heading font-bold text-[#0F2922]">{t('total')}</span>
                    <span className="font-heading text-xl font-bold text-[#B85042]">${getTotalWithDelivery().toFixed(2)} {currency}</span>
                  </div>
                </div>

                {/* Payment Method - Solo PayPal (incluye tarjeta sin cuenta) */}
                <div className="flex gap-3 pt-2">
                  <Button
                    variant="outline"
                    className="flex-1 border-[#E5E3D8] rounded-xl"
                    onClick={() => setCheckoutStep('menu')}
                    data-testid="back-button"
                  >
                    {t('cancel')}
                  </Button>
                </div>
                <div className="pt-2" data-testid="food-paypal-wrapper">
                  <p className="text-xs text-[#5C6C62] mb-2 text-center">
                    {language === 'es' ? `Pago seguro con PayPal o tarjeta (sin necesidad de cuenta). Total:` : language === 'en' ? `Secure payment with PayPal or card (no account required). Total:` : `תשלום מאובטח עם PayPal או כרטיס (ללא צורך בחשבון). סה"כ:`}
                    {' '}<span className="font-bold">${getTotalWithDelivery().toFixed(2)} {currency}</span>
                  </p>
                  <PayPalCheckout
                    amount={1}
                    currency={currency}
                    referenceType="food"
                    description={`Jabad GDL - Pedido ${formData.customerName || ''}`}
                    validate={validateCheckoutForm}
                    beforeCreate={createLocalOrderForPaypal}
                    onSuccess={() => {
                      clearCart();
                      window.location.href = `${window.location.origin}/payment-success?method=paypal`;
                    }}
                  />
                  <TransferPaymentBlock
                    amount={getTotalWithDelivery()}
                    currency={currency}
                    validate={validateCheckoutForm}
                    beforeCreate={createLocalOrderForPaypal}
                    onConfirmed={({ referenceId }) => {
                      clearCart();
                      window.location.href = `${window.location.origin}/order/${referenceId}`;
                    }}
                  />
                </div>
              </CardContent>
            </Card>
            </div>
          </div>
        )}

        {/* Bulk Orders Banner */}
        {checkoutStep === 'menu' && (
          <>
            <div
              className="mb-3 bg-gradient-to-r from-[#B85042]/10 to-[#0F2922]/5 border border-[#B85042]/20 rounded-2xl p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-3"
              data-testid="bulk-orders-banner"
            >
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#B85042]/15 flex items-center justify-center">
                <span className="text-[#B85042] font-bold text-lg">10+</span>
              </div>
              <div className="flex-1">
                <p className="font-body text-sm sm:text-base text-[#0F2922]">
                  <strong>
                    {language === 'es' ? 'Pedidos para mas de 10 personas:'
                      : language === 'en' ? 'Orders for more than 10 people:'
                      : 'הזמנות מעל 10 אנשים:'}
                  </strong>{' '}
                  {language === 'es' ? 'contacta directamente a'
                    : language === 'en' ? 'contact directly'
                    : 'צרו קשר ישירות עם'}
                  {' '}<strong>Elisheva Srugo</strong>.
                </p>
              </div>
              <a
                href="https://wa.me/523331463068"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center px-4 py-2 bg-[#B85042] hover:bg-[#B85042]/90 text-white text-sm font-semibold rounded-xl transition-colors whitespace-nowrap"
                data-testid="bulk-orders-whatsapp"
              >
                +52 33 3146 3068
              </a>
            </div>
            <p
              className="mb-6 text-xs text-[#5C6C62] italic text-center sm:text-left px-2"
              data-testid="image-disclaimer"
            >
              {language === 'es' ? '* Las imagenes son ilustrativas y no representan exactamente el producto final.'
                : language === 'en' ? '* Images are illustrative and do not exactly represent the final product.'
                : '* התמונות הן להמחשה בלבד ואינן מייצגות במדויק את המוצר הסופי.'}
            </p>
          </>
        )}

        {/* Menu Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {menuItems.map((item) => (
            <Card 
              key={item.id} 
              className="bg-white border-[#E5E3D8] rounded-2xl overflow-hidden hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
              data-testid={`menu-item-${item.id}`}
            >
              {item.image_url && (
                <div className="h-48 overflow-hidden">
                  <img 
                    src={item.image_url} 
                    alt={getLocalizedField(item, 'name')}
                    className="menu-img w-full h-full object-cover"
                    loading="lazy"
                    decoding="async"
                  />
                </div>
              )}
              <CardContent className="p-6">
                <h3 className="font-heading text-lg font-bold text-[#0F2922] mb-2">
                  {getLocalizedField(item, 'name')}
                </h3>
                <p className="font-body text-sm text-[#5C6C62] mb-3 line-clamp-2">
                  {getLocalizedField(item, 'description')}
                </p>
                <div className="mb-4">
                  {(getLocalizedField(item, 'ingredients') || []).length > 0 && (
                    <p className="text-xs text-[#5C6C62] font-body">
                      <span className="font-medium">{t('ingredients')}:</span>{' '}
                      {(getLocalizedField(item, 'ingredients') || []).join(', ')}
                    </p>
                  )}
                </div>
                <div className="flex items-center justify-between">
                  <span className="font-heading text-xl font-bold text-[#B85042]">
                    {formatPrice(item.price, item.price_usd)}
                  </span>
                  <Button
                    size="sm"
                    className="bg-[#0F2922] hover:bg-[#0F2922]/90 text-white rounded-xl"
                    onClick={() => {
                      if (item.option_groups?.length > 0 || item.options?.length > 0) {
                        setOptionModal(item);
                      } else {
                        addItem(item);
                        toast.success(`${getLocalizedField(item, 'name')} agregado al carrito`);
                      }
                    }}
                    data-testid={`add-to-cart-${item.id}`}
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    {t('addToCart')}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Option Selection Modal */}
        {optionModal && (
          <OptionGroupsModal
            item={optionModal}
            language={language}
            currency={currency}
            usdRate={USD_RATE}
            onClose={() => setOptionModal(null)}
            onConfirm={(selectedOptions) => {
              addItem({ ...optionModal, selectedOptions });
              toast.success(`${getLocalizedField(optionModal, 'name')} agregado al carrito`);
              setOptionModal(null);
            }}
          />
        )}
      </div>
    </div>
  );
};

export default KosherFoodPage;
